import API from '../Services/API';
import Model from './Model';
import State from './State';

const useAccount = (alwaysRefresh = false, block = false) => {
    const ret = Model({
        block: block,
        stateKey: ['voip', 'account'],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get('/voip/account'),
        returnDataKey: 'account',
        saveAdditionalData: (data) => {
            if (data?.numbers) {
                State.put(['voip', 'numbers'], data.numbers);
                data.numbers.forEach(number => {
                    State.put(['voip', 'numbers', number.id], number);
                });
            }
        },
        registerAdditionalMethods: (opts) => {
            return {
                updateCustomSMSName: (customSMSName) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/voip/updateCustomSMSName', { customSMSName }),
                        null,
                        true,
                    );
                },
                updateSMSSender: (numberId) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/voip/updateSMSSender', { numberId }),
                        null,
                        true,
                    );
                },
                findAvailableNumbers: (search) => {
                    return opts.wrapUpdateCall(
                        () => API.get('/voip/availableNumbers', search),
                        null,
                        true,
                        false,
                    );
                },
                purchaseNumber: (numberDetails) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/voip/numbers/purchase', numberDetails),
                        null,
                        true,
                    );
                },
                updateNumber: (numberId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/voip/numbers/${numberId}`, data),
                        null,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading some data.',
    })

    return {
        ...ret,
        numbers: ret.account?.numbers,
    }
}

const useNumbers = (alwaysRefresh = false, block = false) => {
    return Model({
        block: block,
        stateKey: ['voip', 'numbers'],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get('/voip/numbers'),
        returnDataKey: 'numbers',
        saveAdditionalData: (data) => {
            if (data?.numbers) {
                data.numbers.forEach(number => {
                    State.put(['voip', 'numbers', number.id], number);
                });
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading VoIP numbers.',
    })
}

const useNumberCosts = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        stateKey: ['voip', 'numberCosts'],
        fetchData: () => API.get('/voip/numberCosts'),
        returnDataKey: 'costs',
        defaultLoadingError: 'Sorry, there was an error loading phone number costs.',
    })
}

const useCalls = (searchParams = {}, alwaysRefresh = false, block = false) => {
    const searchKey = JSON.stringify(searchParams);

    return Model({
        block: block,
        stateKey: ['voip', 'calls', searchKey],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get('/voip/calls', searchParams),
        returnDataKey: 'calls',
        defaultLoadingError: 'Sorry, there was an error loading VoIP calls.',
    })
}

const callStatuses = {
    'In Progress': {
        shortName: 'In Progress',
        class: 'warning',
        colors: 'stroke-yellow-600 fill-yellow-600',
        bgColor: 'bg-yellow-100 text-yellow-800',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'fill-yellow-600',
        icon: 'Phone',
        shortDescription: 'Call is currently in progress',
    },
    'Completed': {
        shortName: 'Completed',
        class: 'success',
        colors: 'stroke-green-600 fill-green-600',
        bgColor: 'bg-green-100 text-green-800',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'fill-green-600',
        icon: 'Phone',
        shortDescription: 'Call has been completed',
    },
    'Failed': {
        shortName: 'Failed',
        class: 'danger',
        colors: 'stroke-red-600 fill-red-600',
        bgColor: 'bg-red-100 text-red-800',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'fill-red-600',
        icon: 'PhoneMissed',
        shortDescription: 'Call failed',
    },
};

const callDirections = {
    'Inbound': {
        shortName: 'Inbound',
        class: 'info',
        colors: 'stroke-blue-600 fill-blue-600',
        bgColor: 'bg-blue-100 text-blue-800',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'fill-blue-600',
        icon: 'PhoneIncoming',
        shortDescription: 'Incoming call',
    },
    'Outbound': {
        shortName: 'Outbound',
        class: 'success',
        colors: 'stroke-green-600 fill-green-600',
        bgColor: 'bg-green-100 text-green-800',
        contrastTextColor: 'text-white',
        contrastColor: 'fill-white stroke-white',
        textColor: 'fill-green-600',
        icon: 'PhoneOutgoing',
        shortDescription: 'Outgoing call',
    },
};

const numberTypes = {
    'Mobile': {
        name: 'Mobile',
        sendsSMS: true,
    },
    'Local': {
        name: 'Local',
        sendsSMS: false,
    },
};

const numberStatuses = {
    'Active': {
        name: 'Active',
        theme: 'success',
    },
    'Inactive': {
        name: 'Inactive',
        theme: 'secondary',
    },
    'Suspended': {
        name: 'Suspended',
        theme: 'danger',
    },
};

export {
    useAccount,
    useNumbers,
    useNumberCosts,
    useCalls,
    callStatuses,
    callDirections,
    numberTypes,
    numberStatuses,
}
